import { z } from 'zod';
/**
 * Customer module Zod schemas for API validation.
 *
 * Covers: Company and Customer request schemas.
 */
const customerEntityStatusSchema = z.enum(['active', 'archived', 'merged']);
const customerAttributeTypeSchema = z.enum([
    'text',
    'long_text',
    'select',
    'multi_select',
    'tags',
    'number',
    'boolean',
]);
const customerAttributeSourceTypeSchema = z.enum([
    'manual',
    'meeting_transcript',
    'workspace_note',
    'integration',
    'ai_inference',
]);
const templateAttributeScopeSchema = z.enum(['workspace', 'user']);
const templateAttributeControlSchema = z.string().min(1).max(50).trim();
const templateAttributeAutocompleteSourceSchema = z.string().min(1).max(50).trim();
const templateAttributeOptionSchema = z.object({
    label: z.string().min(1).max(100).trim(),
    value: z.string().min(1).max(100).trim(),
});
const customerAttributeInputSchema = z.object({
    fieldKey: z.string().max(100).trim().optional(),
    name: z.string().min(1, 'Attribute name is required').max(120).trim(),
    fieldType: customerAttributeTypeSchema.optional(),
    control: templateAttributeControlSchema.optional(),
    placeholder: z.string().max(255).trim().optional().nullable(),
    autocompleteSource: templateAttributeAutocompleteSourceSchema.optional().nullable(),
    value: z
        .union([
        z.string().max(10000),
        z.number(),
        z.boolean(),
        z.array(z.string().max(200)),
        z.record(z.string(), z.unknown()),
        z.null(),
    ])
        .optional(),
    description: z.string().max(2000).trim().optional().nullable(),
    aiPrompt: z.string().max(5000).trim().optional().nullable(),
    options: z.array(templateAttributeOptionSchema).max(50).optional().nullable(),
    sectionKey: z.string().max(50).trim().optional(),
    aiEnabled: z.boolean().optional(),
    sourceType: customerAttributeSourceTypeSchema.optional().nullable(),
    sourceRefId: z.string().max(255).trim().optional().nullable(),
    confidenceScore: z.number().min(0).max(1).optional().nullable(),
    saveToUserTemplate: z.boolean().optional(),
});
// ── Company ───────────────────────────────────────────────────────────────────
export const createCompanyRequestSchema = z.object({
    name: z.string().min(1, 'Name is required').max(200).trim(),
    domain: z.string().max(253).trim().optional().nullable(),
    industry: z.string().max(100).trim().optional().nullable(),
});
export const updateCompanyRequestSchema = z.object({
    name: z.string().min(1).max(200).trim().optional(),
    domain: z.string().max(253).trim().optional().nullable(),
    industry: z.string().max(100).trim().optional().nullable(),
    status: customerEntityStatusSchema.optional(),
});
// ── Customer ──────────────────────────────────────────────────────────────────
export const createCustomerRequestSchema = z.object({
    name: z.string().min(1, 'Name is required').max(200).trim(),
    email: z.string().email().max(320).trim().optional().nullable(),
    phone: z.string().max(50).trim().optional().nullable(),
    attributes: z.array(customerAttributeInputSchema).max(100).optional(),
    sourceType: z.enum(['manual', 'crm', 'csv', 'integration', 'ai']).optional(),
});
export const updateCustomerRequestSchema = z.object({
    name: z.string().min(1).max(200).trim().optional(),
    email: z.string().email().max(320).trim().optional().nullable(),
    phone: z.string().max(50).trim().optional().nullable(),
    attributes: z.array(customerAttributeInputSchema).max(100).optional(),
    status: customerEntityStatusSchema.optional(),
});
export const upsertTemplateAttributesRequestSchema = z.object({
    attributes: z.array(z.object({
        fieldKey: z.string().max(100).trim().optional(),
        name: z.string().min(1).max(120).trim(),
        fieldType: customerAttributeTypeSchema.optional(),
        control: templateAttributeControlSchema.optional(),
        placeholder: z.string().max(255).trim().optional().nullable(),
        autocompleteSource: templateAttributeAutocompleteSourceSchema.optional().nullable(),
        description: z.string().max(2000).trim().optional().nullable(),
        aiPrompt: z.string().max(5000).trim().optional().nullable(),
        options: z.array(templateAttributeOptionSchema).max(50).optional().nullable(),
        sectionKey: z.string().max(50).trim().optional(),
        aiEnabled: z.boolean().optional(),
        scope: templateAttributeScopeSchema.optional(),
        displayOrder: z.number().int().min(0).max(1000).optional(),
    })),
});
//# sourceMappingURL=customer.js.map